import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Translate from "../../../Components/Multilanguage/Translate";
import * as service from "./service";

const ConfirmParentalPinReset = () => {
  const [values, setValues] = useState({ pin: "", confirmPin: "" });
  const [errors, setErrors] = useState({});
  const [showPin, setShowPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const accessToken = useSelector((state) => state.accessToken?.value);
  const projectInfo = useSelector((state) => state?.projectInfo?.value);

  const appInfo = {
    accessToken,
    projectDetails: projectInfo,
  };

  const requestId =
    location?.state?.requestId || sessionStorage.getItem("parentalPinResetRequestId");

  useEffect(() => {
    if (!requestId) {
      navigate("/parental-settings", { replace: true });
    }
  }, [requestId, navigate]);

  const handleUpdate = (event) => {
    const { name, value } = event?.target;

    const numericValue = value.replace(/\D/g, '');

    setValues((prev) => ({
      ...prev,
      [name]: numericValue,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: null,
    }));
  };

  const validatePin = () => {
    const { pin, confirmPin } = values;
    const error = {};
    let isValid = true;

    if (!pin) {
      error.pin = <Translate textKey={"parental_pin_required"} />;
      isValid = false;
    } else if (!/^\d{4}$/.test(pin)) {
      error.pin = <Translate textKey={"parental_pin_4_digit_required"} />;
      isValid = false;
    }

    if (pin !== confirmPin) {
      error.confirmPin = <Translate textKey={"parental_pin_not_matching"} />;
      isValid = false;
    }

    setErrors(error);
    return isValid;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      if (!validatePin()) {
        return;
      }

      const response = await service.confirmParentalPinReset(
        appInfo,
        requestId,
        values?.pin
      );

      if (response?.status === 200 || response?.success === true) {
        toast.success(response?.data?.message || response?.message, {
          position: "top-center",
        });
        sessionStorage.removeItem("parentalPinResetRequestId");
        setTimeout(() => {
          navigate(
            projectInfo?.projectConfig?.config?.WEBSITE_NEW_CHECKOUT_REQUIRED == "true"
              ? "/checkout"
              : "/login",
            { state: { activeLogin: true } }
          );
        }, 2000);
      } else {
        toast.error(response?.data?.message || response?.message, {
          position: "top-center",
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong", {
        position: "top-center",
      });
    }
  };

  return (
    <div
      className="resetPassword parentalPinConfirmPage"
      role="main"
      aria-label="Create New Parental PIN Page"
    >
      <ToastContainer />
      <div className="wrapper">
        <h1 className="heading"><Translate textKey={"reset_parental_pin"} /></h1>
        <div className="contents">
          <div className="formSection parentActionCard">
            <form onSubmit={handleSubmit}>
              <div className="inputContainer">
                <label htmlFor="newParentalPin"><Translate textKey={"new_parental_pin"} /></label>
                <div className="parentalPinInputWrap">
                  <input
                    id="newParentalPin"
                    type={showPin ? "text" : "password"}
                    name="pin"
                    maxLength={4}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="new-password"
                    value={values?.pin || ""}
                    onChange={handleUpdate}
                    aria-label="New PIN"
                  />
                  <button
                    type="button"
                    className="pinVisibilityToggle"
                    onClick={() => setShowPin((prev) => !prev)}
                    aria-label={showPin ? "Hide New PIN" : "Show New PIN"}
                  >
                    <i
                      className={showPin ? "fa fa-eye-slash" : "fa fa-eye"}
                      aria-hidden="true"
                    ></i>
                  </button>
                </div>
                {errors.pin && <span className="error">{errors.pin}</span>}
              </div>

              <div className="inputContainer">
                <label htmlFor="confirmParentalPin"><Translate textKey={"confirm_parental_pin"} /></label>
                <div className="parentalPinInputWrap">
                  <input
                    id="confirmParentalPin"
                    type={showConfirmPin ? "text" : "password"}
                    name="confirmPin"
                    maxLength={4}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="new-password"
                    value={values?.confirmPin || ""}
                    onChange={handleUpdate}
                    aria-label="Confirm PIN"
                  />
                  <button
                    type="button"
                    className="pinVisibilityToggle"
                    onClick={() => setShowConfirmPin((prev) => !prev)}
                    aria-label={showConfirmPin ? "Hide Confirm PIN" : "Show Confirm PIN"}
                  >
                    <i
                      className={showConfirmPin ? "fa fa-eye-slash" : "fa fa-eye"}
                      aria-hidden="true"
                    ></i>
                  </button>
                </div>
                {errors.confirmPin && <span className="error">{errors.confirmPin}</span>}
              </div>

              <div className="buttonContainer">
                <button
                  type="submit"
                  className="parentalPrimaryButton"
                  aria-label="Reset Parental PIN"
                >
                  <Translate textKey={"reset_password"} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmParentalPinReset;
